import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  ChevronRight, ChevronDown, Sparkles, ShieldCheck, Heart, BookOpen, Target,
  Map, Trophy, Palette, Users, Baby, Check, PlayCircle, Compass,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

/* Direct-response landing v2 — foco: mãe cristã, mobile-first, checkout Eduzz. */

const HERO_MOCKUP =
  "https://images.pexels.com/photos/8535230/pexels-photo-8535230.jpeg?auto=compress&cs=tinysrgb&w=900";
const SECTION_FAMILY =
  "https://images.pexels.com/photos/7269494/pexels-photo-7269494.jpeg?auto=compress&cs=tinysrgb&w=940";

const PAINS = [
  "Quero ensinar mais sobre Deus, mas nem sempre sei por onde começar.",
  "Quando tento explicar, ele perde o interesse.",
  "Minha rotina é corrida e não consigo preparar atividades.",
  "Queria criar mais momentos de fé dentro de casa.",
];

const METHOD = [
  ["01", "DESCOBRIR", "Histórias e ensinamentos apresentados de maneira apropriada para a infância."],
  ["02", "PARTICIPAR", "Atividades, missões e experiências tiram a criança da posição de apenas assistir."],
  ["03", "CONQUISTAR", "Jornadas, estrelas e o Passaporte da Fé tornam cada descoberta parte de uma caminhada."],
  ["04", "COMPARTILHAR", "Momentos em família ajudam pais e filhos a conversarem sobre aquilo que aprenderam."],
];

const INSIDE = [
  [BookOpen, "Biblioteca da Fé", "Conteúdos organizados para facilitar novas descobertas."],
  [PlayCircle, "Séries", "Histórias organizadas em episódios para continuar aprendendo."],
  [Map, "Jornadas da Fé", "Experiências em sequência que estimulam a criança a avançar."],
  [Target, "Missões", "Pequenos desafios que transformam aprendizado em participação."],
  [Trophy, "Passaporte da Fé", "Progresso e conquistas durante a jornada."],
  [Palette, "Atividades e materiais", "Experiências que também podem continuar fora da tela."],
  [Heart, "Momento em família", "Ideias para pais e filhos compartilharem juntos."],
  [Users, "Área dos Pais", "Recursos para o responsável acompanhar e orientar a experiência."],
];

const HIGHLIGHTS = [
  "Biblioteca da Fé",
  "Histórias cristãs",
  "Séries",
  "Jornadas",
  "Missões",
  "Atividades",
  "Materiais para imprimir",
  "Passaporte da Fé",
  "Sistema de conquistas",
  "Momentos em família",
  "Área dos Pais",
  "Conteúdos para diferentes fases da infância",
];

const FAQ = [
  ["Para qual idade é o Clubinho?", "O Clubinho possui experiências pensadas para diferentes fases da infância, permitindo que os responsáveis encontrem conteúdos adequados para seus filhos."],
  ["Preciso acompanhar meu filho?", "A participação dos pais é incentivada, principalmente nas experiências em família. Dependendo da idade, a criança também poderá explorar conteúdos com maior autonomia sob orientação do responsável."],
  ["É só vídeo?", "Não. O Clubinho reúne histórias, séries, atividades, missões, jornadas, materiais e experiências em família."],
  ["Meu filho já frequenta a igreja. Ainda faz sentido?", "Sim. A proposta é complementar aquilo que ele já aprende e criar oportunidades para que a fé também esteja presente durante a semana e dentro de casa."],
  ["É uma compra única?", "Não. O Clubinho da Fé funciona por assinatura de R$29,90/mês."],
  ["Como recebo acesso?", "Após a confirmação da assinatura, você recebe as instruções de ativação no e-mail utilizado na compra."],
  ["O pagamento é seguro?", "O pagamento é processado pela Eduzz."],
];

export default function Landing() {
  const { user } = useAuth();
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/config/public");
        setCheckoutUrl(data?.eduzz_checkout_url || "");
      } catch { /* ok */ }
    })();
    const onScroll = () => setShowSticky(window.scrollY > 720);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Reutiliza EXATAMENTE a mesma fonte de verdade já usada nas versões
  // anteriores (Fase 6+): /api/config/public → eduzz_checkout_url.
  // Sem checkoutUrl → CTA cai na página institucional /assinatura.
  const cta = user
    ? { to: "/app", external: false, label: "IR PARA O CLUBINHO" }
    : (checkoutUrl
        ? { to: checkoutUrl, external: true, label: "QUERO LEVAR O CLUBINHO PARA MINHA FAMÍLIA" }
        : { to: "/assinatura", external: false, label: "QUERO ASSINAR O CLUBINHO" });

  const Cta = ({ testid, label, className = "btn-primary", size = "md" }) => {
    const sizeCls = size === "lg"
      ? "text-base sm:text-lg px-6 sm:px-8 py-4"
      : "text-sm sm:text-base px-5 py-3";
    const cls = `${className} inline-flex items-center justify-center gap-2 rounded-full font-extrabold ${sizeCls}`;
    const text = label || cta.label;
    if (cta.external) {
      return (
        <a
          href={cta.to}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={testid}
          className={cls}
        >
          {text} <ChevronRight size={18} />
        </a>
      );
    }
    return (
      <Link to={cta.to} data-testid={testid} className={cls}>
        {text} <ChevronRight size={18} />
      </Link>
    );
  };

  return (
    <main className="text-[#243B53]">
      {/* ---------- HEADER ---------- */}
      <header className="sticky top-0 z-40 backdrop-blur bg-[#FFF9ED]/90 border-b border-[#243B53]/10">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <span className="text-2xl">🌞</span>
            <span className="font-extrabold text-lg text-[#243B53]">Clubinho <span className="text-[#D9A441]">da Fé</span></span>
          </Link>
          <div className="flex items-center gap-2">
            {!user && (
              <Link to="/login" data-testid="header-login" className="text-sm font-bold text-[#243B53]/80 px-3 py-2 rounded-full hover:bg-[#243B53]/5">
                Entrar
              </Link>
            )}
            <Cta testid="header-cta" label="QUERO ASSINAR" />
          </div>
        </div>
      </header>

      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden bg-[#FFF9ED]">
        <div className="max-w-6xl mx-auto px-4 pt-10 pb-16 sm:pt-16 sm:pb-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 chip chip-sun" data-testid="hero-eyebrow">
              <Sparkles size={14} /> EDUCAÇÃO CRISTÃ QUE AS CRIANÇAS AMAM
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Seu filho pode aprender sobre Deus enquanto se diverte. <span className="text-[#D9A441]">💛</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#243B53]/80 max-w-xl">
              Histórias, atividades, missões e jornadas cristãs para ajudar você a tornar a fé parte da rotina do seu filho — de um jeito simples, divertido e feito para a infância.
            </p>
            <div className="mt-7">
              <Cta testid="hero-cta" size="lg" />
              <p className="mt-3 text-sm font-bold text-[#243B53]/70">
                R$29,90/mês • Pagamento seguro pela Eduzz
              </p>
            </div>
            <ul className="mt-6 space-y-1.5 text-sm text-[#243B53]/80">
              {[
                "Experiência cristã infantil",
                "Atividades para fazer em família",
                "Ambiente pensado para crianças",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <Check size={16} className="text-[#7BC67B]" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-[#FFD54F]/50 blur-3xl" aria-hidden />
            <div className="absolute -bottom-8 -right-6 w-48 h-48 rounded-full bg-[#5DADE2]/30 blur-3xl" aria-hidden />
            <img
              data-testid="hero-image"
              src={HERO_MOCKUP}
              alt="Família explorando o Clubinho da Fé"
              loading="eager"
              className="relative rounded-[2rem] w-full object-cover aspect-[4/5] sm:aspect-[16/12] shadow-xl border border-[#243B53]/10"
            />
            <div className="absolute -bottom-5 left-4 sm:left-6 card-soft p-4 flex items-center gap-3 bg-white">
              <Trophy className="text-[#D9A441]" />
              <div>
                <div className="text-[10px] uppercase tracking-wide text-[#243B53]/60 font-bold">Passaporte da Fé</div>
                <div className="text-sm font-extrabold">Desperta a curiosidade</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- IDENTIFICAÇÃO COM A DOR ---------- */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            Você também sente que está cada vez mais difícil disputar a atenção dos nossos filhos?
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#243B53]/80">
            Eles crescem cercados por vídeos, jogos, desenhos e estímulos o tempo inteiro. Enquanto isso, muitos pais carregam uma preocupação silenciosa: <b>como fazer a fé também ocupar um lugar especial nessa fase da vida?</b>
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {PAINS.map((p, i) => (
              <div key={i} data-testid={`pain-${i}`} className="card-soft p-4 sm:p-5 bg-[#FFF9ED]">
                <p className="text-sm sm:text-base italic text-[#243B53]/80">“{p}”</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <h3 className="text-2xl sm:text-3xl font-extrabold">Talvez o problema não seja falta de interesse.</h3>
            <p className="mt-3 text-lg sm:text-xl font-extrabold text-[#D9A441]">
              Talvez a fé só precise chegar até a criança de uma maneira feita para a infância.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- NOVA OPORTUNIDADE ---------- */}
      <section className="py-16 sm:py-20 bg-[#FFF9ED]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            E se aprender sobre Deus também pudesse ser um dos momentos que seu filho <span className="text-[#D9A441]">espera</span> durante o dia?
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#243B53]/80 max-w-2xl mx-auto">
            O Clubinho transforma histórias e princípios cristãos em experiências participativas.
            Não é sobre <em>mais conteúdo</em>. É sobre uma nova forma de colocar a fé na rotina.
          </p>
        </div>
      </section>

      {/* ---------- APRESENTAÇÃO DO PRODUTO ---------- */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold">Conheça o Clubinho da Fé <span className="text-[#D9A441]">💛</span></h2>
            <p className="mt-4 text-base sm:text-lg text-[#243B53]/80 max-w-2xl mx-auto">
              Um cantinho cristão criado para transformar o aprendizado da fé em uma experiência que a criança pode descobrir, participar e viver.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {[
              ["📖", "Histórias"],
              ["🎯", "Missões"],
              ["🗺️", "Jornadas"],
              ["🎨", "Atividades"],
              ["🎬", "Séries"],
              ["⭐", "Conquistas"],
              ["🖨️", "Materiais"],
              ["❤️", "Momentos em família"],
            ].map(([e, l], i) => (
              <div key={l} data-testid={`product-item-${i}`} className="card-soft p-4 sm:p-5 text-center bg-[#FFF9ED]">
                <div className="text-3xl">{e}</div>
                <div className="mt-2 text-sm sm:text-base font-extrabold">{l}</div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-[#243B53]/75 max-w-2xl mx-auto">
            Tudo dentro de uma experiência cristã pensada para crianças e suas famílias.
          </p>
          <div className="mt-8 flex justify-center">
            <Cta testid="product-cta" label="QUERO CONHECER O CLUBINHO" size="lg" />
          </div>
        </div>
      </section>

      {/* ---------- MECANISMO ÚNICO (fundo azul profundo) ---------- */}
      <section className="py-16 sm:py-24 bg-[#243B53] text-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 text-xs font-extrabold tracking-widest uppercase text-[#FFD54F]">
              <Compass size={14} /> MECANISMO ÚNICO
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold">
              Conheça o Método <span className="text-[#FFD54F]">Fé que Cresce</span><sup className="text-xs">™</sup>
            </h2>
            <p className="mt-4 text-white/80 max-w-2xl mx-auto">
              No Clubinho da Fé, a criança não entra apenas para assistir.
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {METHOD.map(([n, t, d]) => (
              <div key={n} className="rounded-2xl p-6 bg-white/5 backdrop-blur border border-white/10">
                <div className="text-3xl font-extrabold text-[#FFD54F]">{n}</div>
                <div className="mt-2 text-lg font-extrabold">{t}</div>
                <p className="mt-2 text-sm text-white/75">{d}</p>
              </div>
            ))}
          </div>
          <p className="mt-12 text-center text-xl sm:text-2xl font-extrabold text-[#FFD54F]">
            Ela descobre. Participa. Conquista. Compartilha.
          </p>
        </div>
      </section>

      {/* ---------- DEMONSTRAÇÃO ---------- */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-center">Um Clubinho inteiro para explorar.</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {INSIDE.map(([Icon, t, d], i) => (
              <div key={t} data-testid={`inside-${i}`} className="card-soft p-5 sm:p-6 bg-[#FFF9ED] hover:shadow-md transition-shadow">
                <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center border border-[#243B53]/10">
                  <Icon className="text-[#D9A441]" size={22} />
                </div>
                <div className="mt-4 font-extrabold text-lg">{t}</div>
                <p className="mt-2 text-sm text-[#243B53]/75">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- DESEJO ---------- */}
      <section className="py-16 sm:py-20 bg-[#FFF9ED]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            Imagine ouvir seu filho perguntar:<br />
            <span className="text-[#D9A441]">“Mãe, qual é a próxima missão?”</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#243B53]/80">
            Em vez de transformar o aprendizado da fé em mais uma obrigação, queremos torná-lo um momento mais convidativo para a criança. E você não precisa preparar uma aula inteira.
          </p>
          <div className="mt-10 grid sm:grid-cols-3 gap-4 items-stretch">
            {["Abra o Clubinho.", "Escolha uma experiência.", "Viva esse momento com seu filho."].map((step, i) => (
              <div key={i} className="card-soft p-5 bg-white flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-[#FFD54F] text-[#243B53] font-extrabold flex items-center justify-center">
                  {i + 1}
                </div>
                <p className="mt-3 font-extrabold">{step}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-xl sm:text-2xl font-extrabold">
            Porque ensinar a fé não precisa parecer uma obrigação.
          </p>
        </div>
      </section>

      {/* ---------- OBJEÇÃO — TEMPO DE TELA ---------- */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-extrabold">
            “Mas meu filho já passa muito tempo na tela...”
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#243B53]/80"><b>Nós entendemos.</b></p>
          <p className="mt-3 text-base sm:text-lg text-[#243B53]/80">
            Por isso, a proposta do Clubinho não é simplesmente colocar a criança diante de outra tela.
            A plataforma pode ser o começo da experiência. Depois vêm atividades, conversas, materiais e momentos que podem continuar fora dela e junto da família.
          </p>
          <div className="mt-8 card-soft p-6 sm:p-8 bg-[#243B53] text-white text-center">
            <p className="text-xl sm:text-2xl font-extrabold text-[#FFD54F]">
              A tecnologia é a porta. A experiência de fé vai além dela.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- POSICIONAMENTO DOS PAIS ---------- */}
      <section className="py-16 sm:py-20 bg-[#FFF9ED]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-extrabold">
            Você não precisa ser professor para ensinar aquilo que acredita.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#243B53]/80">
            O Clubinho foi criado para ajudar famílias que querem participar mais da formação cristã dos filhos, mas nem sempre sabem o que ensinar, como explicar ou qual atividade preparar.
          </p>
          <blockquote className="mt-8 card-soft p-6 sm:p-8 bg-white border-l-4 border-[#D9A441]">
            <p className="text-lg sm:text-xl font-extrabold">
              “O Clubinho não substitui os pais no ensino da fé. Ele coloca ferramentas nas mãos deles.”
            </p>
          </blockquote>
        </div>
      </section>

      {/* ---------- MARCA EMOCIONAL ---------- */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
          <img
            src={SECTION_FAMILY}
            alt="Família lendo em casa"
            loading="lazy"
            className="rounded-[2rem] w-full aspect-[4/3] object-cover shadow-lg border border-[#243B53]/10"
          />
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight">
              Enquanto eles crescem, plante. <span className="text-[#7BC67B]">🌱</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-[#243B53]/80">A infância passa.</p>
            <p className="mt-3 text-base sm:text-lg text-[#243B53]/80">
              As histórias mudam. Os brinquedos mudam. Os interesses mudam.
            </p>
            <p className="mt-3 text-base sm:text-lg text-[#243B53]/80">
              Mas os momentos que você cria ao lado do seu filho podem acompanhá-lo por muito tempo.
            </p>
            <p className="mt-3 text-base sm:text-lg text-[#243B53]/80">
              Aproveite essa fase para conversar, ensinar, brincar e construir memórias de fé.
            </p>
            <p className="mt-6 text-2xl sm:text-3xl font-extrabold text-[#D9A441]">
              Enquanto eles crescem, plante.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- OFERTA ---------- */}
      <section className="py-16 sm:py-24 bg-[#243B53] text-white">
        <div className="max-w-3xl mx-auto px-4">
          <div className="rounded-[2rem] p-6 sm:p-10 bg-white text-[#243B53] shadow-2xl border border-white/20">
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-extrabold">
                SUA FAMÍLIA NO CLUBINHO DA FÉ <span className="text-[#D9A441]">💛</span>
              </h2>
            </div>
            <ul className="mt-6 grid sm:grid-cols-2 gap-x-4 gap-y-2">
              {HIGHLIGHTS.map((h) => (
                <li key={h} className="flex items-start gap-2 text-sm sm:text-base">
                  <Check size={18} className="text-[#7BC67B] flex-shrink-0 mt-0.5" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 text-center">
              <p className="text-sm text-[#243B53]/70">Tudo isso por</p>
              <div className="mt-1 text-5xl sm:text-6xl font-extrabold text-[#D9A441]">R$29,90<span className="text-2xl">/mês</span></div>
              <p className="mt-2 text-sm text-[#243B53]/70">
                Cerca de R$1 por dia para ter um ambiente cristão preparado para sua família.
              </p>
              <div className="mt-6">
                <Cta testid="offer-cta" label="QUERO ASSINAR O CLUBINHO DA FÉ" size="lg" />
              </div>
              <div className="mt-4 flex flex-wrap justify-center gap-3 text-xs sm:text-sm text-[#243B53]/70">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} /> Pagamento seguro processado pela Eduzz</span>
                <span className="inline-flex items-center gap-1.5"><Heart size={14} className="text-[#D9A441]" /> Acesso após a confirmação da assinatura</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-center">Perguntas frequentes</h2>
          <Accordion type="single" collapsible className="mt-8">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={i} value={`item-${i}`} className="card-soft mb-3 px-4 bg-[#FFF9ED]">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-extrabold text-left">
                  {q}
                </AccordionTrigger>
                <AccordionContent className="text-[#243B53]/75">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ---------- CTA FINAL ---------- */}
      <section className="py-16 sm:py-24 bg-[#FFF9ED]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <span className="text-4xl">💛</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold leading-tight">
            A infância dele está acontecendo agora.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#243B53]/80">
            Você não precisa esperar ter mais tempo, preparar a aula perfeita ou descobrir sozinho como transformar cada ensinamento em algo interessante para uma criança.
          </p>
          <h3 className="mt-8 text-2xl sm:text-3xl font-extrabold">Comece com um pequeno momento de fé.</h3>
          <p className="mt-2 text-base sm:text-lg text-[#243B53]/80">E deixe o Clubinho ajudar nos próximos.</p>
          <div className="mt-8">
            <Cta testid="final-cta" label="QUERO LEVAR O CLUBINHO PARA MINHA FAMÍLIA" size="lg" />
            <p className="mt-3 font-extrabold text-[#243B53]/80">R$29,90/mês</p>
          </div>
          {!user && (
            <p className="mt-6 text-sm text-[#243B53]/70">
              Já é assinante? <Link to="/login" data-testid="final-login" className="text-[#5DADE2] font-bold">Entrar no Clubinho</Link>
            </p>
          )}
        </div>
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer className="border-t border-[#243B53]/10 py-8 text-center text-sm text-[#243B53]/60 bg-white">
        <div className="flex flex-wrap gap-3 justify-center mb-3">
          <Link to="/privacy" data-testid="footer-privacy-link" className="text-[#243B53]/80 font-bold hover:text-[#5DADE2]">Política de Privacidade</Link>
          <span className="text-[#243B53]/30">•</span>
          <Link to="/terms" data-testid="footer-terms-link" className="text-[#243B53]/80 font-bold hover:text-[#5DADE2]">Termos de Uso</Link>
        </div>
        <div>© {new Date().getFullYear()} Clubinho da Fé — feito com 💛 para famílias cristãs.</div>
        <div className="mt-1 text-xs text-[#243B53]/50">CREATIVE SHOOP — CNPJ 65.465.060/0001-25</div>
      </footer>

      {/* ---------- STICKY CTA MOBILE ---------- */}
      {!user && showSticky && (
        <div
          data-testid="sticky-cta-wrapper"
          className="fixed bottom-3 left-3 right-3 z-50 sm:hidden animate-in fade-in slide-in-from-bottom-2"
        >
          <Cta testid="sticky-cta" label="ASSINAR — R$29,90/mês" className="btn-primary shadow-2xl w-full" />
        </div>
      )}
    </main>
  );
}
